import type { Node as PmNode } from "@tiptap/pm/model";
import { Fragment, Slice } from "@tiptap/pm/model";
import type { ResolvedPos } from "@tiptap/pm/state";

export interface MarkImportedOrderedListOptions {
  isNewInstance?: boolean;
}

/** Mark an imported <ol> so it does not auto-continue prior document numbering. */
export function markImportedOrderedListElement(
  ol: HTMLOListElement,
  options: MarkImportedOrderedListOptions = {},
): void {
  if (options.isNewInstance) {
    ol.setAttribute("data-prevent-auto-continue", "true");
  }

  const startAttr = ol.getAttribute("start");
  const start = startAttr ? parseInt(startAttr, 10) : 1;
  if (!Number.isNaN(start) && start > 1) {
    ol.setAttribute("start", String(start));
  } else if (options.isNewInstance) {
    ol.removeAttribute("start");
  }
}

/**
 * Mark Word/DOCX-imported ordered lists. Skips lists copied from the editor
 * (`data-lexgat-editor-origin`) so in-document copy/paste can still continue.
 */
export function markImportedOrderedListsInRoot(root: ParentNode): void {
  root.querySelectorAll("ol").forEach((node) => {
    if (!(node instanceof HTMLOListElement)) return;
    if (node.hasAttribute("data-prevent-auto-continue")) return;
    if (node.hasAttribute("data-lexgat-editor-origin")) return;
    markImportedOrderedListElement(node, { isNewInstance: true });
  });
}

export function isInsideOrderedList($from: ResolvedPos): boolean {
  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.name === "orderedList") {
      return true;
    }
  }
  return false;
}

function resolvePastedOrderedListStart(node: PmNode): number {
  const start = node.attrs.start;
  return typeof start === "number" && start > 0 ? start : 1;
}

export interface PreparePastedOrderedListOptions {
  /** When true, pasted lists join the surrounding LexGat numbering sequence. */
  adoptSurroundingNumbering?: boolean;
}

function remapOrderedListsForPaste(
  fragment: Fragment,
  options: PreparePastedOrderedListOptions,
): Fragment {
  const children: PmNode[] = [];

  fragment.forEach((node) => {
    if (node.type.name === "orderedList") {
      const content = remapOrderedListsForPaste(node.content, options);

      if (options.adoptSurroundingNumbering) {
        children.push(
          node.type.create(
            {
              ...node.attrs,
              start: 1,
              preventAutoContinue: false,
              editorOrigin: true,
            },
            content,
          ),
        );
        return;
      }

      const start = resolvePastedOrderedListStart(node);

      children.push(
        node.type.create(
          {
            ...node.attrs,
            start,
          },
          content,
        ),
      );
      return;
    }

    if (node.content.size > 0) {
      children.push(node.copy(remapOrderedListsForPaste(node.content, options)));
      return;
    }

    children.push(node);
  });

  return Fragment.from(children);
}

/** Ensure pasted lists use the correct numbering mode for their paste context. */
export function preparePastedOrderedListSlice(
  slice: Slice,
  options: PreparePastedOrderedListOptions = {},
): Slice {
  return new Slice(
    remapOrderedListsForPaste(slice.content, options),
    slice.openStart,
    slice.openEnd,
  );
}
